import * as React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classNames from 'classnames';
import { Theme } from 'helpers/theme';
import UserSearch from 'components/UserSearch';
import { fetchQuery, graphql } from 'relay-runtime';
import { createFragmentContainer } from 'react-relay';
import environment from 'api/environment';
import { HeaderMenuDelegatesQueryResponse } from './__generated__/HeaderMenuDelegatesQuery.graphql';
import { HeaderMenu_manager } from './__generated__/HeaderMenu_manager.graphql';
import Button from 'components/Button/Button';
import { useSideModalDispatch } from 'views/SideModalProvider';
import { useDelegateDetailsDispatch } from 'views/DelegateDetailsProvider';
import SingleUser from 'components/SingleUser';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    background: 'white',
    width: '100%',
    height: 65,
    borderBottom: '1px solid #ededed',
    display: 'flex',
    alignItems: 'center',
    padding: '0px 14px'
  },
  content: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginRight: 44
  },
  leftItems: {
    minWidth: 530
  },
  rightItems: {},
  search: {
    position: 'absolute',
    top: 10,
    width: 350
  },
  searchFocused: {
    extend: 'search',
    width: 520
  },
  mainButtons: {
    display: 'inline-grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(2)
  }
}));

type Props = {
  className?: string;
  manager: HeaderMenu_manager;
};

function HeaderMenu({ className, manager }: Props) {
  const theme = useTheme();
  const classes = useStyles({ theme });

  const [isSingleUserOpen, setSingleUserOpen] = React.useState(false);
  const [searchFocus, setSearchFocus] = React.useState(false);

  const sideModalDispatch = useSideModalDispatch();
  const openSideModal = () => {
    sideModalDispatch({
      type: 'show'
    });
  };

  const delegateModalDispatch = useDelegateDetailsDispatch();
  const openDelegateSlideIn = () => {
    delegateModalDispatch({
      type: 'show'
    });
  };

  return (
    <header className={classNames(classes.root, className)}>
      <div className={classes.content}>
        <div className={classes.leftItems}>
          <div className={searchFocus ? classes.searchFocused : classes.search}>
            <UserSearch
              onFocusChange={(val) => setSearchFocus(val)}
              companyName={manager.company.name}
              searchFunction={async (text: string) => {
                const query = graphql`
                  query HeaderMenuDelegatesQuery($name: String!) {
                    delegates(filter: { name: $name }, page: { limit: 8 }) {
                      edges {
                        uuid
                        TTC_ID
                        firstName
                        lastName
                      }
                    }
                  }
                `;

                const variables = {
                  name: text
                };

                const data = (await fetchQuery(
                  environment,
                  query,
                  variables
                )) as HeaderMenuDelegatesQueryResponse;

                if (!data || !data.delegates || !data.delegates.edges) {
                  console.error('Could not get data', data);
                  return [];
                }

                const results = data.delegates?.edges.map((delegate) => ({
                  uuid: delegate?.uuid ?? '',
                  key: `${delegate?.firstName} ${delegate?.lastName}`,
                  value: delegate?.TTC_ID ?? ''
                }));

                return results;
              }}
            />
          </div>
        </div>
        <div className={classes.rightItems}>
          <div className={classes.mainButtons}>
            <Button bold archetype="submit" onClick={openSideModal}>
              Quick Booking
            </Button>
            <Button
              bold
              archetype="mainGrey"
              onClick={() => {
                openDelegateSlideIn();
              }}
            >
              Add Delegates
            </Button>
          </div>
        </div>
      </div>
      <SingleUser
        user={{ firstName: 'John', uuid: '0000' }}
        isOpen={isSingleUserOpen}
        onClose={() => setSingleUserOpen(false)}
      />
    </header>
  );
}

export default createFragmentContainer(HeaderMenu, {
  manager: graphql`
    fragment HeaderMenu_manager on Manager {
      company {
        name
      }
    }
  `
});
